import { useMemo, useState } from 'react';
import type { CartLine, Product } from '../api';
import { formatMoney, selectionProblems, unitPrice } from '../cart';
import type { T } from '../i18n';
import { ItemImage } from './ItemImage';

type Props = {
  product: Product;
  currency: string | null;
  maxQty: number;
  imageUrl: string | null;
  t: T;
  onAdd: (line: Omit<CartLine, 'key'>) => void;
  onClose: () => void;
};

export function ProductSheet({ product, currency, maxQty, imageUrl, t, onAdd, onClose }: Props) {
  const [variantId, setVariantId] = useState<string | null>(product.variants.length === 1 ? product.variants[0].id : null);
  const [picked, setPicked] = useState<Map<string, number>>(new Map());
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');

  const addons = useMemo(() => [...picked.entries()].filter(([, n]) => n > 0).map(([id, n]) => (n > 1 ? { id, quantity: n } : { id })), [picked]);
  const problems = selectionProblems(product, variantId, picked);
  const price = unitPrice(product, variantId, addons) * qty;

  const toggle = (groupId: string, addonId: string) => {
    const group = product.addon_groups.find((g) => g.id === groupId)!;
    setPicked((prev) => {
      const next = new Map(prev);
      const single = group.max === 1;
      if (single) for (const a of group.addons) if (a.id !== addonId) next.delete(a.id);
      next.set(addonId, prev.get(addonId) ? 0 : 1);
      return next;
    });
  };

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="product-title" onClick={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose} aria-label={t('close')}>×</button>
        <ItemImage src={imageUrl} large />
        <h2 id="product-title">{product.name}</h2>
        {product.description && <p className="note">{product.description}</p>}
        {(product.dietary_tags.length > 0 || product.allergens.length > 0) && (
          <div className="tags">
            {product.dietary_tags.map((tag) => <span className="tag" key={tag}>{tag}</span>)}
            {product.allergens.length > 0 && <span className="tag">{t('allergens', { list: product.allergens.join(', ') })}</span>}
          </div>
        )}

        {product.variants.length > 0 && (
          <fieldset className="group" style={{ border: 0, padding: 0 }}>
            <legend className="group-head" style={{ width: '100%' }}>
              {t('chooseOne')} <small>{t('required')}</small>
            </legend>
            {product.variants.map((v) => (
              <label className="option" key={v.id}>
                <input type="radio" name="variant" checked={variantId === v.id} onChange={() => setVariantId(v.id)} />
                <span>{v.name}</span>
                <strong>{formatMoney(v.price, currency)}</strong>
              </label>
            ))}
          </fieldset>
        )}

        {product.addon_groups.map((g) => {
          const min = Math.max(g.min, g.required ? 1 : 0);
          return (
            <fieldset className="group" key={g.id} style={{ border: 0, padding: 0 }}>
              <legend className="group-head" style={{ width: '100%' }}>
                {g.name}{' '}
                <small>
                  {min > 0 ? (g.max === 1 ? t('chooseOne') : t('chooseAtLeast', { n: min })) : ''}
                  {g.max && g.max > 1 ? ` · ${t('chooseUpTo', { n: g.max })}` : ''}
                </small>
              </legend>
              {g.addons.map((a) => (
                <label className="option" key={a.id}>
                  <input type={g.max === 1 ? 'radio' : 'checkbox'} name={`g-${g.id}`} checked={(picked.get(a.id) ?? 0) > 0} onChange={() => toggle(g.id, a.id)} />
                  <span>{a.name}</span>
                  {a.price > 0 && <span>+{formatMoney(a.price, currency)}</span>}
                </label>
              ))}
            </fieldset>
          );
        })}

        <label className="lbl" htmlFor="item-notes">{t('instructions')}</label>
        <textarea id="item-notes" rows={2} maxLength={300} placeholder={t('instructionsPlaceholder')} value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '16px 0' }}>
          <span>{t('quantity')}</span>
          <span className="qty">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label={t('decrease')}>−</button>
            <span aria-live="polite">{qty}</span>
            <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} aria-label={t('increase')}>+</button>
          </span>
        </div>

        <button
          className="btn"
          disabled={!product.available || problems.length > 0}
          onClick={() => onAdd({ product_id: product.id, variant_id: variantId, quantity: qty, addons, special_instructions: notes.trim() })}
        >
          {product.available ? t('addToOrder', { price: formatMoney(price, currency) }) : t('soldOut')}
        </button>
      </div>
    </div>
  );
}
